import {
  find,
  flatten,
  flow,
  get,
  isEmpty,
  last,
  map,
  times,
  some,
  pickBy,
  negate,
  isNil,
  filter,
} from 'lodash';
import { sortBy } from 'lodash/fp';
import moment from 'moment-timezone';

import { parseTimeSlug, mapTimes } from '../utils/helpers';
import config from '../../config';

const getTimes = state => get(state, 'times.items');

export const getTimesForDay = (state, day) => {
  const items = get(state.times, 'items', []);
  const findTimesForDay = item => item.day === day;
  return get(items.find(findTimesForDay), 'times', []).map(mapTimes);
};

export const getActiveDayTimes = state => getTimesForDay(state, state.times.activeDay);

const isDayActive = (state, day) => day === state.times.activeDay;

const dayHasTimes = (state, day) => getTimesForDay(state, day).length > 0;

const getDay = (state, day) => {
  const dayOfWeek = moment(day).format('ddd');
  const formatted = moment(day).format('D');

  const date = day.format(config.apiDateFormat);

  return {
    dayOfWeek,
    formatted,
    active: isDayActive(state, date),
    disabled: !dayHasTimes(state, date),
    date,
  };
};

export const getDays = state => map(
  state.times.items,
  item => getDay(state, moment(item.day, config.apiDateFormat)),
);

const getDaysForWeek = (state, startDay = moment()) =>
  times(7, i => getDay(state, moment(startDay).add(i, 'days')));

export const getDaysUpToAdvanceBookDays = (state, advanceBookDays = 7) => {
  const weeks = Math.ceil(advanceBookDays / 7) || 1;
  return flatten(times(weeks, i => getDaysForWeek(state, moment().add(i, 'week'))));
};

export const shouldLoadTimesForInterval = (state, startDay, endDay) => {
  if (!startDay || !endDay) return false;

  return (
    !some(get(state, 'times.items'), item => item.day === startDay) ||
    !some(get(state, 'times.items'), item => item.day === endDay)
  );
};

export const getNoTimesAvailable = state => getActiveDayTimes(state).length === 0;

export const getNextUnfetchedDay = (state) => {
  const lastFetchedDay = get(last(state.times.items), 'day');
  if (!lastFetchedDay) return null;
  return moment(lastFetchedDay).add(1, 'days').format(config.apiDateFormat);
};

export const getNextUnfetchedDayUpToAdvanceBookDays = (state, advanceBookDays) => {
  const nextUnfetchedDay = getNextUnfetchedDay(state);
  if (moment(nextUnfetchedDay).isBefore(moment().add(advanceBookDays, 'days'))) return nextUnfetchedDay;
  return null;
};

const getSelectedTime = (state, reservationTime, timezone) => {
  const selected = parseTimeSlug(reservationTime, timezone);
  if (isEmpty(selected)) return null;
  const selectedDay = selected.format(config.apiDateFormat);
  const selectedTimes = get(
    find(state.times.items, { day: selectedDay }), 'times'
  );
  return find(selectedTimes, { time: selected.toISOString() });
};

export const getSelectedTimeBarberRoute = (state, reservationTime, timezone) => {
  const selectedTime = getSelectedTime(state, reservationTime, timezone);
  if (isEmpty(selectedTime)) return null;
  return get(selectedTime, 'barberRoute');
};

export const getActiveDay = state => get(state, 'times.activeDay');

export const getActiveTime = state => get(state, 'times.activeTime');

export const getActiveTimeBarber = state => get(state, 'times.activeTimeBarber', '');

export const getTimeOrItem = item => (get(item, 'time') ? item.time : item);
const sortTimeItems = sortBy(getTimeOrItem);
export const getNextAvailableTime = (state, fromDate, timezone) => {
  const from = moment.tz(fromDate, timezone).startOf('day');
  const isTimeAfter = time => moment(time).isAfter(from);
  return flow(
    getTimes,
    items => map(items, 'times'),
    flatten,
    sortTimeItems,
    items => find(items, flow(getTimeOrItem, isTimeAfter)),
  )(state);
};

export const getNextLink = (state, generateUrlString, anyBarber) => {
  const reservationTime = getActiveTime(state);
  const barberRoute = getActiveTimeBarber(state);
  const query = anyBarber ? { anyBarber: true } : null;

  return {
    pathname: generateUrlString(pickBy({ reservationTime, barberRoute }, negate(isNil))),
    query,
  };
};

export const getIsWholeDayOff = (state, day) => {
  const wholeDaysOff = filter(state.daysOff.get('items'), 'wholeDay');
  const daysOff = map(wholeDaysOff, item => moment.utc(item.day).format('YYYY-MM-DD'));
  return daysOff.includes(day);
};



// WEBPACK FOOTER //
// ./src/app/times/selectors.js